import { Alert, Box, MenuItem, TextField, Typography } from '@mui/material';
import { Ban, Download, LogOut, RotateCcw, UserX } from 'lucide-react';
import { useState, type ReactElement } from 'react';

import {
  createAdminApi,
  roleSatisfies,
  type AdminRole,
  type AdminUserDetail,
  type SensitiveActionInput,
} from '@/admin/adminApi';
import {
  SensitiveActionDialog,
  type SensitiveActionConsequence,
} from '@/admin/SensitiveActionDialog';
import { SENSITIVE_ACTIONS } from '@/admin/sensitiveActions';
import { babyName, describeSessionCount } from '@/admin/users/accountPresentation';
import { adminErrorCode } from '@/admin/settings/adminErrorMessage';
import { messageOf } from '@/admin/users/adminErrors';
import { useHttpClient } from '@/app/providers';
import { Button, Card } from '@/ui/kit';

import { EXPORT_LIMITATION, EXPORT_NOT_A_COMPLETE_ANSWER } from './exportLimitation';

/**
 * Zone sensible de la fiche : les actions qu'un administrateur peut exercer sur
 * le compte d'un tiers.
 *
 * Six regles gouvernent ce panneau, et aucune n'est negociable :
 *
 * 1. **Le role commande ce qui est propose.** Un `support` ne voit **aucune**
 *    action, pas meme une explication : le panneau n'existe pas pour lui. Ce
 *    n'est pas une securite — le serveur tranche, et il tranchera de la meme
 *    facon quoi qu'affiche cet ecran (ADR 0032) — c'est de l'honnetete : un
 *    bouton qui mene a un 403 est un ecran qui ment.
 * 2. **Un compte anonymise n'a plus de zone sensible.** Il est en lecture seule :
 *    plus de session a couper, plus d'adresse a confirmer, aucune suspension a
 *    poser, rien a exporter. Seul le message de la derniere action reussie reste
 *    affiche — l'anonymisation vient de la.
 * 3. **Une seule barriere pour toutes**, `SensitiveActionDialog` : motif type,
 *    saisie exacte de l'adresse visee, consequence ecrite avant le geste, et mot
 *    de passe de l'operateur pour l'export, la suspension et l'anonymisation.
 *    Ecrire une confirmation maison pour l'une d'elles reviendrait a la proteger
 *    autrement, donc moins bien.
 * 4. **Le mot « Supprimer » n'apparait nulle part.** Ce que le systeme sait
 *    faire est une anonymisation : la ligne de compte demeure, et avec elle la
 *    tracabilite des lots de lait (ADR 0011). Nommer cela autrement serait un
 *    mensonge d'interface, et ici un mensonge a portee juridique.
 * 5. **Un echec n'efface pas l'ecran.** Le message — traduit par `apiError.ts` —
 *    s'affiche DANS la boite, qui reste ouverte avec le motif deja saisi. Un
 *    403 en particulier ne deconnecte personne : le compte est authentifie, il
 *    n'a simplement plus le role.
 * 6. **L'export exige un profil bebe**, choisi avant d'ouvrir la boite : c'est
 *    ce que la route reclame, et cela evite d'afficher une boite dont on ne
 *    saurait pas remplir le corps.
 */

type ActionKind = 'sessions' | 'suspend' | 'reactivate' | 'export' | 'anonymize';

interface Outcome {
  readonly severity: 'success' | 'info';
  readonly message: string;
}

/**
 * Nom du fichier produit, quand la reponse en porte un.
 *
 * La reponse est un `unknown` : on ne suppose pas sa forme. Sans nom lisible,
 * le message reste vrai — il dit que l'export existe — au lieu d'afficher
 * « undefined ».
 */
const exportFilename = (body: unknown): string | null => {
  if (typeof body !== 'object' || body === null) {
    return null;
  }

  const filename: unknown = (body as Record<string, unknown>).filename;

  return typeof filename === 'string' && filename !== '' ? filename : null;
};

/** Nombre de sessions reellement revoquees, quand le serveur le dit. */
const revokedCount = (body: unknown): number | null => {
  if (typeof body !== 'object' || body === null) {
    return null;
  }

  const count: unknown = (body as Record<string, unknown>).revokedSessionCount;

  return typeof count === 'number' && Number.isFinite(count) ? count : null;
};

const SESSIONS_CONSEQUENCE = (user: AdminUserDetail): SensitiveActionConsequence => ({
  summary: `${describeSessionCount(user.activeSessionCount)} actuellement.`,
  disappears: [
    'Les sessions de tous ses appareils : la personne devra saisir à nouveau son mot de passe',
    'Les notifications envoyées à ces appareils',
  ],
  kept: [
    'Ses saisies, ses lots de lait et leur traçabilité',
    'Son compte et son mot de passe, inchangés',
  ],
});

const SUSPEND_CONSEQUENCE = (user: AdminUserDetail): SensitiveActionConsequence => ({
  summary: (
    <>
      <strong>La suspension est réversible.</strong> La personne ne peut plus se connecter ni
      synchroniser tant qu’elle dure ; {describeSessionCount(user.activeSessionCount).toLowerCase()}{' '}
      actuellement.
    </>
  ),
  disappears: [
    'La connexion : la personne ne peut plus ouvrir de session ni se reconnecter',
    'Les sessions et les appareils connectés, coupés immédiatement',
  ],
  kept: [
    'Ses saisies, ses lots de lait et leur traçabilité',
    'Ses partages : les coparents gardent leur accès aux profils bébé',
    'La possibilité de lever la suspension à tout moment',
  ],
  disappearsTitle: 'Est coupé',
});

const REACTIVATE_CONSEQUENCE: SensitiveActionConsequence = {
  summary:
    'Rend l’accès à la personne. Aucune session n’est recréée : elle se reconnecte avec son mot de passe, et son application rejoue ce qu’elle avait gardé en attente.',
  disappears: [],
  kept: [
    'Toutes ses données, intactes : la suspension n’en avait retiré aucune',
    'Son mot de passe, inchangé',
  ],
};

const EXPORT_CONSEQUENCE = (profileName: string): SensitiveActionConsequence => ({
  summary: (
    <>
      Un export des données du profil <strong>{profileName}</strong> est produit{' '}
      <strong>au nom du compte visé</strong>, comme si la personne l’avait demandé elle-même. Il
      contient des données de santé de son enfant, et laisse une trace nominative au journal : qui
      l’a demandé, quand, pour quel compte et pour quel motif.{' '}
      <strong>
        {EXPORT_LIMITATION} {EXPORT_NOT_A_COMPLETE_ANSWER}
      </strong>
    </>
  ),
  // Rien ne disparait : la colonne n'est pas rendue plutot que d'afficher une
  // croix devant un fichier qui, lui, apparait.
  disappears: [],
  kept: [
    'Toutes les données du compte, intactes : l’export n’en retire rien',
    'La maîtrise du fichier : il n’est envoyé à personne et se télécharge depuis l’administration seulement',
  ],
});

/**
 * Le texte le plus important de tout l'ecran.
 *
 * Le pire mode d'echec de cette fiche est un administrateur qui **croit
 * detruire ce qu'il ne detruit pas**. Le texte dit donc les deux moities : ce
 * qui part, et ce qui reste. Il evite aussi le verbe « supprimer », qui
 * decrirait une operation que le systeme ne sait pas faire.
 */
const ANONYMIZE_CONSEQUENCE: SensitiveActionConsequence = {
  summary: (
    <>
      <strong>Cette opération est irréversible.</strong> Elle rend le compte anonyme : elle
      n’efface ni le compte lui-même, ni les données de suivi de la famille. La personne ne pourra
      plus se connecter, ni récupérer son compte.
    </>
  ),
  disappears: [
    'Nom, prénom et photo de profil',
    'Adresse e-mail, remplacée par une adresse technique',
    'Appareils connectés et exports déjà produits',
  ],
  kept: [
    'Lots de lait et toute leur traçabilité',
    'Mouvements de stock, tirages, repas et mesures de croissance',
    'Profils bébé et leurs autres membres',
  ],
};

export const AccountSensitiveActions = ({
  user,
  role,
  operatorEmail,
  onChanged,
}: {
  user: AdminUserDetail;
  role: AdminRole;
  /** Adresse du compte d'exploitation connecte, pour le gestionnaire de mots de passe. */
  operatorEmail: string;
  /** Rappelé après une action réussie : la fiche doit être relue, pas devinée. */
  onChanged: () => void;
}): ReactElement | null => {
  const http = useHttpClient();
  const [action, setAction] = useState<ActionKind | null>(null);
  const [pending, setPending] = useState(false);
  const [dialogError, setDialogError] = useState<string | null>(null);
  const [dialogErrorCode, setDialogErrorCode] = useState<string | null>(null);
  const [outcome, setOutcome] = useState<Outcome | null>(null);
  const [chosenBabyId, setChosenBabyId] = useState<string | null>(null);

  // Le profil exporte se deduit de la fiche a chaque rendu : une relecture qui
  // retire un profil ne doit pas laisser un identifiant orphelin dans l'etat.
  const selectedBaby = user.babies.find((baby) => baby.id === chosenBabyId) ?? user.babies[0] ?? null;
  const suspended = user.suspendedAt !== null;
  const administrator = user.adminRole !== null;

  const outcomeAlert =
    outcome === null ? null : (
      <Alert severity={outcome.severity} variant="outlined">
        {outcome.message}
      </Alert>
    );

  // Regles 1 et 2 : ni `support`, ni compte anonymise ne voient d'action. Le
  // message de l'action qui vient d'aboutir reste lisible — c'est lui qui dit
  // pourquoi les boutons viennent de disparaitre.
  if (!roleSatisfies(role, 'admin') || user.anonymized) {
    return outcomeAlert;
  }

  const perform = async (kind: ActionKind, input: SensitiveActionInput): Promise<Outcome> => {
    const api = createAdminApi(http);

    if (kind === 'sessions') {
      const body = await api.revokeSessions(user.id, input);
      const count = revokedCount(body);

      return {
        severity: 'success',
        message:
          count === null
            ? 'Les appareils de ce compte ont été déconnectés.'
            : `${describeSessionCount(count)} au moment du geste : tous ont été déconnectés.`,
      };
    }

    if (kind === 'suspend') {
      await api.suspendUser(user.id, input);

      return {
        severity: 'success',
        message: 'Le compte est suspendu et ses appareils sont déconnectés. La suspension peut être levée à tout moment.',
      };
    }

    if (kind === 'reactivate') {
      await api.reactivateUser(user.id, input);

      return {
        severity: 'success',
        message: 'La suspension est levée. La personne peut se reconnecter avec son mot de passe.',
      };
    }

    if (kind === 'anonymize') {
      await api.anonymizeUser(user.id, input);

      return {
        severity: 'success',
        message:
          'Le compte est désormais anonyme. Son adresse a été remplacée par une adresse technique, et les données de suivi de la famille sont conservées.',
      };
    }

    // `babyProfileId` est obligatoire : le service d'export du projet n'exporte
    // que par profil bebe, et l'omettre rend `422 validation_failed`.
    const body = await api.requestExport(user.id, { ...input, babyProfileId: selectedBaby?.id ?? '' });
    const filename = exportFilename(body);

    return {
      severity: 'success',
      message:
        filename === null
          ? 'L’export a été produit. Il est consultable depuis l’administration.'
          : `L’export « ${filename} » a été produit. Il est consultable depuis l’administration.`,
    };
  };

  const confirm = (input: SensitiveActionInput): void => {
    const kind = action;

    if (kind === null) {
      return;
    }

    setPending(true);
    setDialogError(null);
    setDialogErrorCode(null);

    perform(kind, input)
      .then((result) => {
        setPending(false);
        setAction(null);
        setOutcome(result);
        onChanged();
      })
      .catch((error: unknown) => {
        // La boite reste ouverte, motif compris : un refus se corrige, il
        // n'efface pas ce qu'on venait d'ecrire — et surtout pas la fiche.
        setPending(false);
        setDialogError(messageOf(error));
        setDialogErrorCode(adminErrorCode(error));
      });
  };

  const open = (kind: ActionKind): void => {
    setOutcome(null);
    setDialogError(null);
    setDialogErrorCode(null);
    setAction(kind);
  };

  const cancel = (): void => {
    setAction(null);
    setDialogError(null);
    setDialogErrorCode(null);
  };

  const dialogProps = {
    operatorEmail,
    expectedConfirmation: user.email,
    confirmationLabel: 'l’adresse e-mail du compte',
    pending,
    error: dialogError,
    errorCode: dialogErrorCode,
    onCancel: cancel,
    onConfirm: confirm,
  } as const;

  return (
    <>
      {outcomeAlert}
      <Card
        title="Zone sensible"
        subtitle="Chaque action demande un motif et la saisie de l’adresse e-mail du compte ; l’export, la suspension et l’anonymisation, votre mot de passe. Toutes sont enregistrées au journal."
      >
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
            <Button
              icon={LogOut}
              onClick={() => {
                open('sessions');
              }}
            >
              Déconnecter tous les appareils
            </Button>
            <Typography sx={{ fontSize: 12, color: 'tokens.textMuted' }}>
              {describeSessionCount(user.activeSessionCount)}. Aucune donnée n’est touchée.
            </Typography>
          </Box>

          {administrator ? (
            <Typography sx={{ fontSize: 13, color: 'tokens.textMuted' }}>
              Ce compte porte un rôle d’administration : il ne peut être ni suspendu ni anonymisé tant que ce rôle
              n’est pas retiré en console.
            </Typography>
          ) : (
            <>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                {suspended ? (
                  <Button
                    icon={RotateCcw}
                    onClick={() => {
                      open('reactivate');
                    }}
                  >
                    Réactiver le compte
                  </Button>
                ) : (
                  <Button
                    icon={Ban}
                    onClick={() => {
                      open('suspend');
                    }}
                  >
                    Suspendre le compte
                  </Button>
                )}
                <Typography sx={{ fontSize: 12, color: 'tokens.textMuted' }}>
                  {suspended
                    ? 'Rend l’accès à la personne, qui se reconnecte avec son mot de passe.'
                    : 'Réversible. Coupe la connexion et les appareils ; aucune donnée n’est touchée.'}
                </Typography>
              </Box>
            </>
          )}

          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
            {selectedBaby === null ? (
              <Typography sx={{ fontSize: 13, color: 'tokens.textMuted' }}>
                Ce compte n’a créé aucun profil bébé : il n’y a rien à exporter.
              </Typography>
            ) : (
              <>
                <TextField
                  select
                  size="small"
                  label="Profil bébé à exporter"
                  helperText="Un export porte sur un profil bébé, jamais sur le compte entier."
                  value={selectedBaby.id}
                  onChange={(event) => {
                    setChosenBabyId(event.target.value);
                  }}
                >
                  {user.babies.map((baby) => (
                    <MenuItem key={baby.id} value={baby.id}>
                      {babyName(baby)}
                    </MenuItem>
                  ))}
                </TextField>
                <Button
                  icon={Download}
                  onClick={() => {
                    open('export');
                  }}
                >
                  Déclencher un export
                </Button>
              </>
            )}
          </Box>

          {administrator ? null : (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
              <Button
                variant="danger"
                icon={UserX}
                onClick={() => {
                  open('anonymize');
                }}
              >
                Anonymiser ce compte
              </Button>
              <Typography sx={{ fontSize: 12, color: 'tokens.textMuted' }}>
                Irréversible. Le nom, l’adresse et la photo partent ; les lots de lait et leur traçabilité restent.
              </Typography>
            </Box>
          )}
        </Box>
      </Card>

      <SensitiveActionDialog
        {...dialogProps}
        open={action === 'sessions'}
        title="Déconnecter tous les appareils"
        subtitle={user.email}
        consequence={SESSIONS_CONSEQUENCE(user)}
        reasonOptions={SENSITIVE_ACTIONS.revoke_sessions.reasons}
        requiresPassword={SENSITIVE_ACTIONS.revoke_sessions.requiresPassword}
        actionLabel="Déconnecter les appareils"
      />

      <SensitiveActionDialog
        {...dialogProps}
        open={action === 'suspend'}
        title="Suspendre le compte"
        subtitle={`${user.email} · réversible`}
        consequence={SUSPEND_CONSEQUENCE(user)}
        reasonOptions={SENSITIVE_ACTIONS.suspend.reasons}
        requiresPassword={SENSITIVE_ACTIONS.suspend.requiresPassword}
        actionLabel="Suspendre le compte"
      />

      <SensitiveActionDialog
        {...dialogProps}
        open={action === 'reactivate'}
        title="Réactiver le compte"
        subtitle={user.email}
        consequence={REACTIVATE_CONSEQUENCE}
        reasonOptions={SENSITIVE_ACTIONS.reactivate.reasons}
        requiresPassword={SENSITIVE_ACTIONS.reactivate.requiresPassword}
        actionLabel="Réactiver le compte"
      />

      <SensitiveActionDialog
        {...dialogProps}
        open={action === 'export'}
        title="Déclencher un export"
        subtitle={`${user.email} · données de santé`}
        consequence={EXPORT_CONSEQUENCE(selectedBaby === null ? 'sélectionné' : babyName(selectedBaby))}
        reasonOptions={SENSITIVE_ACTIONS.export.reasons}
        requiresPassword={SENSITIVE_ACTIONS.export.requiresPassword}
        actionLabel="Produire l’export"
      />

      <SensitiveActionDialog
        {...dialogProps}
        open={action === 'anonymize'}
        title="Anonymiser ce compte"
        subtitle={`${user.email} · irréversible`}
        consequence={ANONYMIZE_CONSEQUENCE}
        reasonOptions={SENSITIVE_ACTIONS.anonymize.reasons}
        requiresPassword={SENSITIVE_ACTIONS.anonymize.requiresPassword}
        actionLabel="Anonymiser définitivement"
      />
    </>
  );
};
